/**
 * Agentic Payment Transaction History - Shared Styles
 *
 * Centralized style objects consistent with the Airdrop design system.
 */

// ============================================================================
// Color Palette (same as Airdrop for consistency)
// ============================================================================

export const colors = {
    primary: "#2e3b61",
    primaryLight: "#3d4d7a",
    accent: "#e58734",
    accentDark: "#d77a2a",
    success: "#28a745",
    successDark: "#218838",
    danger: "#dc3545",
    dangerDark: "#c82333",
    warning: "#ffc107",
    warningText: "#856404",
    warningBg: "#fff3cd",
    info: "#17a2b8",
    infoDark: "#138496",
    white: "#fff",
    black: "#000",
    gray100: "#f8f9fa",
    gray200: "#f5f5f5",
    gray300: "#e0e0e0",
    gray400: "#ccc",
    gray500: "#999",
    gray600: "#666",
    gray700: "#333",
    bgLight: "#fafafa",
    bgMedium: "#f0f0f0",
    border: "#e0e0e0",
    borderLight: "#ddd",
};

// ============================================================================
// Gradients
// ============================================================================

export const gradients = {
    primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    accent: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
    success: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
    danger: `linear-gradient(135deg, ${colors.danger} 0%, ${colors.dangerDark} 100%)`,
    info: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoDark} 100%)`,
    gray: `linear-gradient(135deg, #6c757d 0%, #5a6268 100%)`,
};

// ============================================================================
// Page Header Styles
// ============================================================================

export const pageHeaderStyles = {
    container: {
        background: gradients.primary,
        padding: "28px 32px",
        borderRadius: "8px 8px 0 0",
        marginBottom: "0",
    },
    title: {
        margin: "0 0 12px 0",
        fontSize: "28px",
        fontWeight: 700,
        color: colors.white,
        letterSpacing: "0.3px",
    },
    subtitle: {
        margin: 0,
        fontSize: "14px",
        color: "rgba(255,255,255,0.75)",
    },
};

// ============================================================================
// Common Styles
// ============================================================================

export const commonStyles = {
    panel: {
        background: colors.white,
        borderRadius: "6px",
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
    },
    panelHeader: {
        margin: 0,
        padding: "16px 20px",
        background: gradients.primary,
        color: colors.white,
        fontSize: "16px",
        fontWeight: 600,
        borderBottom: `2px solid ${colors.accent}`,
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 24px",
        background: gradients.primary,
        borderRadius: "6px 6px 0 0",
        marginBottom: "20px",
        borderBottom: `3px solid ${colors.accent}`,
    },
    input: {
        width: "100%",
        padding: "10px 14px",
        border: `1px solid ${colors.gray400}`,
        borderRadius: "4px",
        fontSize: "14px",
        boxSizing: "border-box",
        height: "40px",
    },
    select: {
        width: "100%",
        padding: "10px 32px 10px 14px",
        border: `1px solid ${colors.gray400}`,
        borderRadius: "4px",
        fontSize: "14px",
        boxSizing: "border-box",
        height: "40px",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 10px center",
        backgroundSize: "16px",
    },
    button: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 20px",
        border: "none",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    buttonPrimary: {
        background: gradients.primary,
        color: colors.white,
    },
    buttonAccent: {
        background: gradients.accent,
        color: colors.white,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    buttonGhost: {
        background: "rgba(255,255,255,0.15)",
        color: colors.white,
        border: "1px solid rgba(255,255,255,0.3)",
    },
    buttonDisabled: {
        background: colors.gray300,
        color: colors.gray500,
        cursor: "not-allowed",
    },
    filterBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        background: colors.gray100,
        borderRadius: "6px",
        border: `1px solid ${colors.border}`,
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "12px",
    },
    badge: {
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-block",
    },
    code: {
        background: colors.gray200,
        padding: "4px 8px",
        borderRadius: "3px",
        fontSize: "12px",
        fontFamily: "monospace",
    },
    label: {
        display: "block",
        marginBottom: "8px",
        fontWeight: 600,
        color: colors.primary,
        fontSize: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
};

// ============================================================================
// Stats Card Styles
// ============================================================================

export const statsCardStyles = {
    container: {
        display: "flex",
        gap: "16px",
        marginBottom: "20px",
        flexWrap: "wrap",
    },
    card: {
        flex: 1,
        minWidth: "140px",
        padding: "16px 20px",
        borderRadius: "6px",
        textAlign: "center",
    },
    value: {
        display: "block",
        fontSize: "28px",
        fontWeight: 700,
        color: colors.white,
    },
    label: {
        fontSize: "12px",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.85)",
        fontWeight: 600,
        letterSpacing: "0.5px",
    },
};

// ============================================================================
// Pagination Styles
// ============================================================================

export const paginationStyles = {
    container: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 16px",
        background: colors.gray200,
        borderTop: `1px solid ${colors.border}`,
        flexWrap: "wrap",
        gap: "12px",
    },
    info: {
        fontSize: "13px",
        color: colors.gray600,
    },
    nav: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    navButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 14px",
        height: "36px",
        fontSize: "14px",
        fontWeight: 600,
        border: "none",
        borderRadius: "4px",
        userSelect: "none",
        cursor: "pointer",
    },
    pageButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "36px",
        height: "36px",
        padding: "0 8px",
        fontSize: "14px",
        borderRadius: "4px",
        userSelect: "none",
        cursor: "pointer",
        border: "none",
    },
};

// ============================================================================
// Modal Styles
// ============================================================================

export const modalStyles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    },
    content: {
        background: colors.white,
        borderRadius: "8px",
        maxWidth: "700px",
        width: "90%",
        maxHeight: "85vh",
        overflow: "auto",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 20px",
        background: gradients.primary,
        borderBottom: `3px solid ${colors.accent}`,
        position: "sticky",
        top: 0,
    },
    headerTitle: {
        margin: 0,
        fontSize: "18px",
        fontWeight: 600,
        color: colors.white,
    },
    closeButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        background: "rgba(255,255,255,0.15)",
        color: colors.white,
        border: "1px solid rgba(255,255,255,0.3)",
        borderRadius: "4px",
        padding: "8px 16px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "1",
        whiteSpace: "nowrap",
    },
    body: {
        padding: "20px",
    },
    detailRow: {
        display: "flex",
        padding: "12px 0",
        borderBottom: `1px solid ${colors.border}`,
    },
    detailLabel: {
        width: "160px",
        fontWeight: 600,
        color: colors.primary,
        fontSize: "13px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        flexShrink: 0,
    },
    detailValue: {
        flex: 1,
        fontSize: "14px",
        color: colors.gray700,
        wordBreak: "break-all",
    },
};

// ============================================================================
// Loading State Styles
// ============================================================================

export const loadingStyles = {
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: colors.gray100,
        borderRadius: "6px",
        border: `1px solid ${colors.border}`,
    },
    spinner: {
        width: "40px",
        height: "40px",
        border: `3px solid ${colors.border}`,
        borderTopColor: colors.primary,
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        marginBottom: "16px",
    },
    text: {
        color: colors.gray600,
        fontSize: "14px",
    },
};

// ============================================================================
// Button Styles (inline, bypasses ActiveAdmin CSS completely)
// ============================================================================

export const buttonStyles = {
    /** Orange gradient CTA button (View Transaction History) */
    viewHistory: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "9px 20px",
        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
        color: colors.white,
        border: "none",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(229,135,52,0.25)",
        textShadow: "none",
        letterSpacing: "0.2px",
        lineHeight: "1.4",
        WebkitAppearance: "none",
        appearance: "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
    },
    viewHistoryHover: {
        transform: "translateY(-1px)",
        boxShadow: "0 4px 14px rgba(229,135,52,0.4)",
    },
    viewHistoryDefault: {
        transform: "translateY(0)",
        boxShadow: "0 2px 8px rgba(229,135,52,0.25)",
    },

    /** Glassmorphism ghost button (Back to Wallets) */
    ghost: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "9px 20px",
        background: "rgba(255,255,255,0.12)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        color: colors.white,
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        textShadow: "none",
        letterSpacing: "0.2px",
        lineHeight: "1.4",
        WebkitAppearance: "none",
        appearance: "none",
        boxShadow: "none",
        transition: "transform 0.2s ease, background 0.2s ease, border-color 0.2s ease",
    },
    ghostHover: {
        background: "rgba(255,255,255,0.22)",
        borderColor: "rgba(255,255,255,0.45)",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
    ghostDefault: {
        background: "rgba(255,255,255,0.12)",
        borderColor: "rgba(255,255,255,0.25)",
        transform: "translateY(0)",
        boxShadow: "none",
    },
};
